import { ActionIcon, Flex, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconZoomIn } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useAuditLogs } from 'hooks/audit'
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  useMantineReactTable,
} from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo, useState } from 'react'
import { AuditAction, AuditLogTableItem, AuditType } from 'types/audit'
import { Account } from 'types/auth'
import { getAuditTypeName, getMessageForAuditAction } from 'utils'

type Props = {
  users: Array<Account>
}

export default function HistoryTable({ users }: Props) {
  function openDetailsModal(auditLog: AuditLogTableItem) {
    modals.openContextModal({
      modal: 'historyDetails',
      centered: true,
      title: 'Szczegóły historii',
      size: 'lg',
      innerProps: { auditLog },
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<AuditLogTableItem>>>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Data',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const createdAt = cell.getValue<Date>()
          return dayjs(createdAt).format('D MMMM HH:mm')
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie',
          locale: 'pl',
          valueFormat: 'D MMMM',
          preserveTime: false,
        },
      },
      {
        accessorKey: 'product.symbol',
        header: 'Produkt',
      },
      {
        accessorKey: 'user.id',
        header: 'Użytkownik',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: users.map((user) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          })),
        },
        Cell: ({ cell }) => {
          const userId = cell.getValue<string>()
          const user = users.find((user) => user.id === userId)
          if (!user) return ''
          return `${user?.firstName} ${user?.lastName}`
        },
      },
      {
        accessorKey: 'auditType',
        header: 'Typ akcji',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: Object.values(AuditType).map((auditType) => ({
            value: auditType,
            label: getAuditTypeName(auditType),
          })),
        },
        Cell: ({ cell }) => {
          const auditType = cell.getValue<AuditType>()
          return getAuditTypeName(auditType)
        },
      },
      {
        accessorKey: 'auditAction',
        header: 'Opis',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: Object.values(AuditAction).map((auditAction) => ({
            value: auditAction,
            label: getMessageForAuditAction(auditAction),
          })),
        },
        Cell: ({ cell }) => {
          const auditAction = cell.getValue<AuditAction>()
          return getMessageForAuditAction(auditAction)
        },
      },
    ],
    []
  )

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const { data, isLoading, isError, error, isFetching } = useAuditLogs({
    columnFilters,
    pagination,
    sorting,
  })
  const memoizedData = useMemo(() => data ?? { data: [], total: 0 }, [JSON.stringify(data)])

  const table = useMantineReactTable({
    columns,
    data: memoizedData.data,
    layoutMode: 'grid',
    rowCount: memoizedData.total,
    state: {
      columnFilters,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
    },
    initialState: {
      showColumnFilters: true,
    },
    enableGlobalFilterModes: true,
    enableSorting: true,
    enableHiding: false,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableBottomToolbar: true,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    autoResetPageIndex: false,
    enableTopToolbar: false,
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${error})`,
        }
      : undefined,
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      rowsPerPageOptions: ['10', '15', '20'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 60,
      },
    },
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Zobacz szczegóły">
          <ActionIcon variant="light" radius="xl" onClick={() => openDetailsModal(row.original)}>
            <IconZoomIn size={18} />
          </ActionIcon>
        </Tooltip>
      </Flex>
    ),
  })

  return <MantineReactTable table={table} />
}
